import * as generator from 'generate-password';
import Immutable from 'immutable';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Icon, Input, Popup } from 'semantic-ui-react';
import { StrictPopupProps } from 'semantic-ui-react/dist/commonjs/modules/Popup/Popup';

import analytics from '../../../../shared/services/analytics/analytics';
import { addModifiers } from '../../../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../../shared/helpers/internationalization';
import HOCIsMobile, { HOCIsMobileState } from '../../../HOCIsMobile/HOCIsMobile';
import ErrorMsgIntl from '../../ErrorMsg/ErrorMsgIntl';
import { usePasswordVisibility } from '../../PasswordInput';
import { PasswordGuideline, PasswordGuidelines } from '../PasswordGuidelines/PasswordGuidelines';

import './PasswordGenerator.scss';

export interface PasswordGeneratorProps {
  name: string;
  label?: string | null;
  value?: string;
  handleChange: (event: any, data: any) => void;
  handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  modifiers?: string[];
  contentModifiers?: string[];
  required?: boolean;
  placeholder?: string;
  errors?: Immutable.List<string>;
  id?: string;
  tabindex?: number;
  autoFocus?: boolean;
  guidelines?: PasswordGuideline[];
  autoComplete?: string;
  guidelinesPosition?: StrictPopupProps['position'];
  gaGenerateEvent?: string;
}

type PasswordGeneratorGenericProps = PasswordGeneratorProps & HOCIsMobileState & InjectedIntlProps;

const PasswordGenerator: React.FC<PasswordGeneratorGenericProps> = ({
  label,
  name,
  value,
  handleChange: onChange,
  handleBlur: onBlurProp,
  modifiers,
  contentModifiers,
  required,
  placeholder,
  errors,
  id,
  tabindex = 0,
  autoFocus,
  autoComplete,
  guidelines,
  guidelinesPosition,
  gaGenerateEvent,
  isMobile,
  intl,
}) => {
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const guidelinesTimeoutRef = useRef<number | null>(null);

  const [score, setScore] = useState<number | string>('null');
  const [passGenerate, setPassGenerate] = useState(false);
  const [isGuidelinesShown, setIsGuidelinesShown] = useState(false);
  const { type, visible: isPasswordVisible, toggle: togglePasswordVisibility, iconName } = usePasswordVisibility();

  const computeStrength = useCallback((next: any) => {
    if (next === '') {
      setScore('null');
    } else {
      import('zxcvbn')
        .then((lazyZxcvbn: any) => {
          const pw = lazyZxcvbn.default(next);
          setScore(pw.score);
        })
        .catch(error => {
          throw error;
        });
    }
  }, []);

  useEffect(() => {
    computeStrength(value);
  }, [value, computeStrength]);

  useEffect(
    () => () => {
      if (guidelinesTimeoutRef.current) {
        window.clearTimeout(guidelinesTimeoutRef.current);
      }
    },
    []
  );

  const handleFocus = (_event: React.FocusEvent<HTMLInputElement>) => {
    setIsGuidelinesShown(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    guidelinesTimeoutRef.current = window.setTimeout(() => {
      setIsGuidelinesShown(false);
    }, 400);
    onBlurProp?.(event);
  };

  const handleChange = (event: any, data: any) => {
    onChange(event, data);
    if (passGenerate) {
      setPassGenerate(false);
    }
  };

  const handleGeneratePassword = (event: any) => {
    if (gaGenerateEvent) {
      analytics.trackCustomEvent(gaGenerateEvent);
    }
    const next = generator.generate({ length: 13, numbers: true });
    onChange(event, { name, value: next, type: null });
    setPassGenerate(true);
  };

  const toggleLabel = formatMessageHelper(intl, isPasswordVisible ? 'password.hide' : 'password.show');
  const scores: any = [
    formatMessageHelper(intl, 'step1.password.strength.veryWeak'),
    formatMessageHelper(intl, 'step1.password.strength.veryWeak'),
    formatMessageHelper(intl, 'step1.password.strength.weak'),
    formatMessageHelper(intl, 'step1.password.strength.strong'),
    formatMessageHelper(intl, 'step1.password.strength.veryStrong'),
  ];
  const inputErrors = errors ?? Immutable.List<string>([]);
  const inputModifiers = modifiers ?? [];
  const inputPlaceholder = placeholder ?? '';
  const inputContentModifiers = contentModifiers ?? [];
  const popUpGuidelinesPosition = guidelinesPosition ?? 'top left';

  const renderGuidelines = () => <PasswordGuidelines password={value} validationErrors={errors} guidelines={guidelines} />;

  return (
    <div className={addModifiers('password-generator__container', inputContentModifiers)}>
      <div className="password-generator__label-content">
        {label && (
          <label htmlFor={id} className="password-generator__label">
            {label}
            {required && <sup className="password-generator__sup">*</sup>}
          </label>
        )}
        {passGenerate && <div className="password-generator__value">{value}</div>}
      </div>
      <div className="password-generator__content">
        <div className="password-generator__content-input">
          {/* In semantic-ui-react v3, `Input` forwards its ref to the underlying */}
          {/* `<input>` element, so we can pass the ref directly to use as `Popup` context. */}
          <Input
            ref={passwordInputRef}
            type={type}
            name={name}
            value={value}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            className={addModifiers('password-generator__input', inputModifiers)}
            placeholder={inputPlaceholder}
            id={id}
            tabIndex={tabindex}
          />
          <button
            type="button"
            className="password-generator__visibility"
            onClick={togglePasswordVisibility}
            aria-label={toggleLabel}
            aria-pressed={isPasswordVisible}
            tabIndex={tabindex}
          >
            <Icon name={iconName} fitted />
          </button>
        </div>
        <button type="button" onClick={handleGeneratePassword} className="password-generator__button">
          <FormattedMessageHelper tagName="span" id={'password.generate'} defaultMessage="Generate" />
        </button>
      </div>
      <div className="password-generator__pass-strength-content" data-score={score}>
        <div className="password-generator__pass-strength">
          <span className="password-generator__pass-strength-value" />
          <span className="password-generator__pass-strength-value" />
          <span className="password-generator__pass-strength-value" />
          <span className="password-generator__pass-strength-value" />
        </div>
        {score !== 'null' && <div className="password-generator__pass-strength-text">{scores[score]}</div>}
      </div>

      <div className="input__error">
        {/* Don't show error when guidelines is being shown to not confuse users */}
        {!isGuidelinesShown && inputErrors.size !== 0 && <ErrorMsgIntl errors={inputErrors} />}
      </div>

      {/* Show guidelines in popup on desktop and show right below the password input for mobile */}
      {isMobile && isGuidelinesShown ? (
        renderGuidelines()
      ) : (
        <Popup
          context={passwordInputRef as React.RefObject<HTMLElement>}
          open={isGuidelinesShown}
          content={renderGuidelines()}
          position={popUpGuidelinesPosition}
        />
      )}
    </div>
  );
};

export default HOCIsMobile(injectIntl(PasswordGenerator));
